import { isNil } from 'lodash'
import {
  ProjectFragment,
  ProjectVersionFragment,
  ProjectVersionFragmentDoc,
  UpdateProjectVersionInput,
  useCreateProjectVersionMutation,
  useUpdateProjectVersionMutation,
  useDeleteProjectVersionMutation,
  PricingModelFragment,
  PricingModelFragmentDoc,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type ProjectVersionWithHierarchy = Pick<
  ProjectVersionFragment,
  'id' | 'baseVersionId'
>

type UseProjectVersions = {
  createProjectVersion: (
    project: ProjectFragment,
    name: string,
    copyOfVersionId?: string,
    omitBaseVersion?: boolean,
  ) => Promise<ProjectVersionFragment>
  updateProjectVersion: (
    id: string,
    input: UpdateProjectVersionInput,
  ) => Promise<ProjectVersionFragment>
  removeProjectVersion: (id: string, project: ProjectFragment) => Promise<void>
}

export function useProjectVersions(): UseProjectVersions {
  const [createProjectVersionMutation] = useCreateProjectVersionMutation()
  const [updateProjectVersionMutation] = useUpdateProjectVersionMutation()
  const [deleteProjectVersionMutation] = useDeleteProjectVersionMutation()
  const cacheHelpers = useCacheHelpers()

  const createProjectVersion = async (
    project: ProjectFragment,
    name: string,
    copyOfVersionId?: string,
    omitBaseVersion?: boolean,
  ) => {
    const result = await createProjectVersionMutation({
      variables: {
        projectId: project.id,
        input: {
          name,
          omitBaseVersion,
        },
        copyOfVersionId,
      },

      update: (_, mutationResult) => {
        const version = mutationResult.data?.createProjectVersion
        if (!version) {
          return
        }

        cacheHelpers.addItemToList(
          ProjectVersionFragmentDoc,
          version,
          project,
          'versions',
        )
      },
    })

    if (!result.data) {
      throw new Error('Project version creation failed')
    }

    return result.data.createProjectVersion
  }

  const updateProjectVersion = async (
    id: string,
    input: UpdateProjectVersionInput,
  ) => {
    const fragment = cacheHelpers.getFragment<ProjectVersionFragment>(
      id,
      ProjectVersionFragmentDoc,
    )
    if (!fragment) {
      throw new Error('Project version not found in cache')
    }

    const pricingModelFragment = isNil(input.pricingModelId)
      ? input.pricingModelId
      : cacheHelpers.getFragment<PricingModelFragment>(
          input.pricingModelId,
          PricingModelFragmentDoc,
        )

    const result = await updateProjectVersionMutation({
      variables: { id, input },
      optimisticResponse: {
        updateProjectVersion: {
          ...fragment,
          name: input.name ?? fragment.name,
          ...(pricingModelFragment && { pricingModel: pricingModelFragment }),
          ...(!isNil(input.isDefault) && { isDefault: input.isDefault }),
        },
      },
    })

    if (!result.data) {
      throw new Error('Project version update failed')
    }

    return result.data.updateProjectVersion
  }

  const removeProjectVersion = async (id: string, project: ProjectFragment) => {
    const result = await deleteProjectVersionMutation({
      variables: { id },

      update: (cache, mutationResult) => {
        if (!mutationResult.data?.deleteProjectVersion) {
          return
        }

        const projectVersionsAfterDelete: ProjectVersionWithHierarchy[] =
          mutationResult.data?.deleteProjectVersion

        const updatedVersions = projectVersionsAfterDelete.map((version) => {
          const currentVersion = project.versions.find(
            (v) => v.id === version.id,
          )
          return { ...currentVersion, baseVersionId: version.baseVersionId }
        })

        cache.modify({
          id: cache.identify(project),
          fields: {
            versions(_currentVersions) {
              return updatedVersions
            },
          },
        })
      },
    })

    if (!result.data) {
      throw new Error('Project version remove failed')
    }
  }

  return {
    createProjectVersion,
    updateProjectVersion,
    removeProjectVersion,
  }
}
